import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { io } from 'socket.io-client';
import { API_URL, SOCKET_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';
import './DataPage.css';

// ─── Helpers ───────────────────────────────────────────────────────────────────

function formatTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function formatDateLabel(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const msgDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diff = (today - msgDay) / (1000 * 60 * 60 * 24);
  if (diff === 0) return 'Hoje';
  if (diff === 1) return 'Ontem';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatRelativeTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return 'agora';
  if (diff < 3600) return `${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const msgDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dayDiff = (today - msgDay) / (1000 * 60 * 60 * 24);
  if (dayDiff === 1) return 'Ontem';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

function formatCountdown(expiresAt) {
  if (!expiresAt) return null;
  const diff = new Date(expiresAt) - new Date();
  if (diff <= 0) return null;
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  if (hours > 0) return `${hours}h ${minutes}min`;
  return `${minutes}min`;
}

function truncateText(text, maxLen) {
  if (!text) return '';
  if (text.length <= maxLen) return text;
  return text.substring(0, maxLen) + '...';
}

function getMessageStyle(tipo, msg) {
  // Mensagens de sistema: abertura, fechamento, follow-up
  if (tipo === 'envio' && msg && msg.origem_tipo === 'sistema') {
    return {
      isSystem: true,
      align: 'center',
      bg: '#fff',
      borderColor: '#e5e7eb',
      radius: '20px',
      label: 'Sistema',
      labelColor: '#6b7280',
      labelBg: '#f3f4f6',
    };
  }

  // Detect IA responses: envio with tipo_msg 'resposta_ia'
  if (tipo === 'envio' && msg && msg.tipo_msg === 'resposta_ia') {
    return {
      align: 'flex-end',
      bg: '#ede9fe',
      borderColor: '#c4b5fd',
      radius: '12px 4px 12px 12px',
      label: msg.sender || 'Agente IA',
      labelColor: '#5b21b6',
      labelBg: '#ddd6fe',
    };
  }

  switch (tipo) {
    case 'recebida':
    case 'user':
      return {
        align: 'flex-start',
        bg: '#dcfce7',
        borderColor: '#bbf7d0',
        radius: '4px 12px 12px 12px',
        label: 'Paciente',
        labelColor: '#166534',
        labelBg: '#bbf7d0',
      };
    case 'envio':
      return {
        align: 'flex-end',
        bg: '#dbeafe',
        borderColor: '#bfdbfe',
        radius: '12px 4px 12px 12px',
        label: 'Laboratorio',
        labelColor: '#1e40af',
        labelBg: '#bfdbfe',
      };
    case 'assistant':
      return {
        align: 'flex-end',
        bg: '#ede9fe',
        borderColor: '#c4b5fd',
        radius: '12px 4px 12px 12px',
        label: (msg && msg.sender) || 'Agente IA',
        labelColor: '#5b21b6',
        labelBg: '#ddd6fe',
      };
    case 'tool':
      return {
        align: 'center',
        bg: '#fef3c7',
        borderColor: '#fde68a',
        radius: '8px',
        label: 'Tool',
        labelColor: '#92400e',
        labelBg: '#fde68a',
      };
    default:
      return {
        align: 'flex-start',
        bg: '#f3f4f6',
        borderColor: '#e5e7eb',
        radius: '8px',
        label: tipo || 'Desconhecido',
        labelColor: '#374151',
        labelBg: '#e5e7eb',
      };
  }
}

function formatRecordingTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// ─── Styles ────────────────────────────────────────────────────────────────────

const styles = {
  wrapper: {
    height: '100vh',
    overflow: 'hidden',
    padding: 0,
    margin: 0,
  },
  container: {
    display: 'flex',
    height: '100%',
    background: '#f0f2f5',
    overflow: 'hidden',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },

  // Left Panel
  leftPanel: {
    width: '340px',
    minWidth: '340px',
    background: '#fff',
    borderRight: '1px solid #e5e7eb',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  leftHeader: {
    padding: '12px 16px',
    background: '#f0f2f5',
    borderBottom: '1px solid #e5e7eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: '56px',
  },
  leftHeaderTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  pageIcon: {
    fontSize: '1.5rem',
    lineHeight: 1,
  },
  pageTitle: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#1a1a2e',
    margin: '0 0 2px 0',
  },
  pageSubtitle: {
    fontSize: '0.85rem',
    color: '#666',
    margin: 0,
  },
  searchContainer: {
    padding: '8px 12px',
    background: '#f0f2f5',
  },
  searchInput: {
    width: '100%',
    padding: '8px 12px 8px 32px',
    border: 'none',
    borderRadius: '8px',
    background: '#fff',
    fontSize: '13px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  searchWrapper: {
    position: 'relative',
  },
  searchIcon: {
    position: 'absolute',
    left: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: '14px',
    color: '#9ca3af',
    pointerEvents: 'none',
  },
  conversationList: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  conversationItem: (isSelected) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    cursor: 'pointer',
    background: isSelected ? '#f0f9ff' : '#fff',
    borderBottom: '1px solid #f3f4f6',
    transition: 'background 0.15s',
  }),
  avatar: {
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    fontWeight: 700,
    color: '#fff',
    flexShrink: 0,
    textTransform: 'uppercase',
  },
  convInfo: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
  },
  convNameRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2px',
  },
  convName: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1f2937',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '160px',
  },
  convTime: {
    fontSize: '11px',
    color: '#9ca3af',
    flexShrink: 0,
  },
  convPhone: {
    fontSize: '12px',
    color: '#6b7280',
    fontFamily: 'monospace',
    marginBottom: '3px',
  },
  convLastMsg: {
    fontSize: '12px',
    color: '#9ca3af',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  convBadges: {
    display: 'flex',
    gap: '4px',
    marginTop: '4px',
  },
  badge: (bg, color) => ({
    fontSize: '10px',
    padding: '1px 6px',
    borderRadius: '10px',
    background: bg,
    color: color,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  }),

  // Right Panel
  rightPanel: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minWidth: 0,
  },
  emptyState: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f0f2f5',
    color: '#667781',
  },
  emptyIcon: {
    fontSize: '80px',
    marginBottom: '20px',
    opacity: 0.5,
  },
  emptyTitle: {
    fontSize: '24px',
    fontWeight: 300,
    color: '#41525d',
    marginBottom: '8px',
  },
  emptySubtitle: {
    fontSize: '14px',
    color: '#667781',
  },

  // Chat Header
  chatHeader: {
    padding: '10px 16px',
    background: '#f0f2f5',
    borderBottom: '1px solid #e5e7eb',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    minHeight: '56px',
  },
  chatHeaderInfo: {
    flex: 1,
    minWidth: 0,
  },
  chatHeaderName: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#1f2937',
  },
  chatHeaderPhone: {
    fontSize: '12px',
    color: '#6b7280',
    fontFamily: 'monospace',
  },
  chatHeaderActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flexShrink: 0,
  },
  windowBadge: (isOpen) => ({
    fontSize: '11px',
    padding: '3px 8px',
    borderRadius: '12px',
    background: isOpen ? '#dcfce7' : '#fee2e2',
    color: isOpen ? '#166534' : '#991b1b',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  }),
  headerBtn: {
    padding: '6px 10px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    background: '#fff',
    cursor: 'pointer',
    fontSize: '13px',
    color: '#374151',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    transition: 'background 0.15s',
    whiteSpace: 'nowrap',
  },
  aiBtn: (enabled) => ({
    padding: '6px 10px',
    border: enabled ? '1px solid #7c3aed' : '1px solid #d1d5db',
    borderRadius: '6px',
    background: enabled ? '#ede9fe' : '#fff',
    cursor: 'pointer',
    fontSize: '13px',
    color: enabled ? '#5b21b6' : '#374151',
    fontWeight: enabled ? 600 : 400,
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    transition: 'all 0.15s',
    whiteSpace: 'nowrap',
  }),

  // Messages Area
  messagesArea: {
    flex: 1,
    overflowY: 'auto',
    padding: '12px 48px',
    background: '#efeae2',
    backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'200\' height=\'200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cdefs%3E%3Cpattern id=\'a\' patternUnits=\'userSpaceOnUse\' width=\'40\' height=\'40\'%3E%3Cpath d=\'M0 20h40M20 0v40\' fill=\'none\' stroke=\'%23d1d5db\' stroke-width=\'.3\' opacity=\'.3\'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width=\'100%25\' height=\'100%25\' fill=\'%23efeae2\'/%3E%3Crect width=\'100%25\' height=\'100%25\' fill=\'url(%23a)\'/%3E%3C/svg%3E")',
  },
  dateSeparator: {
    display: 'flex',
    justifyContent: 'center',
    margin: '16px 0 12px',
  },
  dateSeparatorLabel: {
    background: '#fff',
    padding: '4px 14px',
    borderRadius: '8px',
    fontSize: '12px',
    color: '#54656f',
    fontWeight: 500,
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
  },
  messageRow: (align) => ({
    display: 'flex',
    justifyContent: align,
    marginBottom: '4px',
  }),
  messageBubble: (style, isCompact) => ({
    maxWidth: isCompact ? '85%' : '65%',
    padding: isCompact ? '4px 10px' : '6px 10px 8px',
    borderRadius: style.radius,
    background: style.bg,
    boxShadow: '0 1px 1px rgba(0,0,0,0.08)',
    position: 'relative',
    minWidth: isCompact ? 'auto' : '120px',
  }),
  messageLabel: (style) => ({
    fontSize: '10px',
    fontWeight: 700,
    color: style.labelColor,
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    marginBottom: '2px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  }),
  messageLabelBadge: (bg, color) => ({
    fontSize: '9px',
    padding: '0 5px',
    borderRadius: '3px',
    background: bg,
    color: color,
    fontWeight: 600,
    lineHeight: '16px',
  }),
  messageContent: (isCompact) => ({
    fontSize: isCompact ? '11px' : '13px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    color: '#111827',
    lineHeight: '1.4',
  }),
  messageTime: {
    fontSize: '10px',
    color: '#667781',
    textAlign: 'right',
    marginTop: '2px',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '4px',
  },
  senderBadge: {
    fontSize: '9px',
    color: '#6b7280',
    background: 'rgba(0,0,0,0.06)',
    padding: '1px 5px',
    borderRadius: '3px',
    cursor: 'default',
    fontWeight: 500,
  },
  toolExtra: {
    fontSize: '10px',
    color: '#6b7280',
    marginTop: '3px',
    fontFamily: 'monospace',
    background: 'rgba(255,255,255,0.5)',
    padding: '3px 6px',
    borderRadius: '4px',
    wordBreak: 'break-all',
  },
  mediaImage: {
    maxWidth: '280px',
    maxHeight: '300px',
    borderRadius: '6px',
    marginBottom: '4px',
    cursor: 'pointer',
    display: 'block',
  },
  mediaAudio: {
    maxWidth: '280px',
    marginBottom: '4px',
    display: 'block',
    outline: 'none',
  },
  mediaVideo: {
    maxWidth: '280px',
    maxHeight: '240px',
    borderRadius: '6px',
    marginBottom: '4px',
    display: 'block',
  },
  mediaDocLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    background: 'rgba(255,255,255,0.7)',
    borderRadius: '6px',
    textDecoration: 'none',
    color: '#1e40af',
    fontSize: '13px',
    marginBottom: '4px',
  },

  // Input Area
  inputArea: {
    padding: '8px 16px',
    background: '#f0f2f5',
    borderTop: '1px solid #e5e7eb',
    display: 'flex',
    alignItems: 'flex-end',
    gap: '8px',
  },
  inputAreaDisabled: {
    padding: '12px 16px',
    background: '#f0f2f5',
    borderTop: '1px solid #e5e7eb',
    textAlign: 'center',
  },
  windowClosedWarning: {
    fontSize: '13px',
    color: '#991b1b',
    background: '#fee2e2',
    padding: '8px 16px',
    borderRadius: '8px',
    display: 'inline-block',
  },
  textArea: {
    flex: 1,
    padding: '10px 12px',
    border: 'none',
    borderRadius: '8px',
    background: '#fff',
    fontSize: '14px',
    resize: 'none',
    outline: 'none',
    maxHeight: '120px',
    minHeight: '42px',
    fontFamily: 'inherit',
    lineHeight: '1.4',
    boxSizing: 'border-box',
  },
  iconBtn: {
    width: '42px',
    height: '42px',
    borderRadius: '50%',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    color: '#54656f',
    flexShrink: 0,
    transition: 'background 0.15s',
  },
  sendBtn: {
    width: '42px',
    height: '42px',
    borderRadius: '50%',
    border: 'none',
    background: '#00a884',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    color: '#fff',
    flexShrink: 0,
    transition: 'background 0.15s',
  },
  sendBtnDisabled: {
    width: '42px',
    height: '42px',
    borderRadius: '50%',
    border: 'none',
    background: '#b0b0b0',
    cursor: 'not-allowed',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    color: '#fff',
    flexShrink: 0,
  },

  // Recording indicator
  recordingBar: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 16px',
    background: '#fff',
    borderRadius: '8px',
    height: '42px',
    boxSizing: 'border-box',
  },
  recordingDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    background: '#ef4444',
    animation: 'pulse 1.2s ease-in-out infinite',
    flexShrink: 0,
  },
  recordingTimer: {
    fontSize: '14px',
    color: '#374151',
    fontFamily: 'monospace',
    fontWeight: 600,
  },
  recordingCancel: {
    fontSize: '12px',
    color: '#9ca3af',
    cursor: 'pointer',
    marginLeft: 'auto',
    padding: '4px 8px',
    borderRadius: '4px',
    border: '1px solid #d1d5db',
    background: '#fff',
  },

  // Attach Menu
  attachMenuContainer: {
    position: 'relative',
  },
  attachMenu: {
    position: 'absolute',
    bottom: '50px',
    left: '0',
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    padding: '8px 0',
    minWidth: '180px',
    zIndex: 100,
  },
  attachMenuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 20px',
    cursor: 'pointer',
    fontSize: '14px',
    color: '#374151',
    transition: 'background 0.15s',
    border: 'none',
    background: 'transparent',
    width: '100%',
    textAlign: 'left',
  },

  // Media Preview
  mediaPreviewOverlay: {
    position: 'absolute',
    bottom: '0',
    left: '0',
    right: '0',
    background: '#fff',
    borderTop: '1px solid #e5e7eb',
    padding: '16px',
    zIndex: 50,
  },
  mediaPreviewContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  mediaPreviewHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  mediaPreviewTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1f2937',
  },
  mediaPreviewClose: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#6b7280',
    padding: '4px',
    lineHeight: 1,
  },
  mediaPreviewImage: {
    maxWidth: '100%',
    maxHeight: '200px',
    borderRadius: '8px',
    objectFit: 'contain',
  },
  mediaPreviewFileInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px',
    background: '#f3f4f6',
    borderRadius: '8px',
    fontSize: '13px',
    color: '#374151',
  },
  mediaPreviewCaption: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '13px',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  mediaPreviewActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
  },
  mediaPreviewCancel: {
    padding: '8px 16px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    background: '#fff',
    cursor: 'pointer',
    fontSize: '13px',
    color: '#374151',
  },
  mediaPreviewSend: {
    padding: '8px 16px',
    border: 'none',
    borderRadius: '6px',
    background: '#00a884',
    cursor: 'pointer',
    fontSize: '13px',
    color: '#fff',
    fontWeight: 600,
  },

  // Loading
  loadingOverlay: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#efeae2',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '3px solid #e5e7eb',
    borderTop: '3px solid #00a884',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },

  // Responsive mobile toggle
  mobileBackBtn: {
    padding: '6px 10px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    background: '#fff',
    cursor: 'pointer',
    fontSize: '16px',
    display: 'none',
    color: '#374151',
  },

  // Image lightbox
  lightboxOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    cursor: 'pointer',
  },
  lightboxImage: {
    maxWidth: '90vw',
    maxHeight: '90vh',
    objectFit: 'contain',
    borderRadius: '4px',
  },
  lightboxClose: {
    position: 'absolute',
    top: '16px',
    right: '24px',
    fontSize: '32px',
    color: '#fff',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    lineHeight: 1,
  },

  // Contact Panel
  contactPanel: {
    width: '320px',
    minWidth: '320px',
    background: '#fff',
    borderLeft: '1px solid #e5e7eb',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflowY: 'auto',
  },
  contactPanelHeader: {
    padding: '14px 16px',
    background: '#f0f2f5',
    borderBottom: '1px solid #e5e7eb',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    minHeight: '56px',
  },
  contactPanelClose: {
    background: 'none',
    border: 'none',
    fontSize: '18px',
    cursor: 'pointer',
    color: '#54656f',
    padding: '4px',
    lineHeight: 1,
    borderRadius: '4px',
  },
  contactPanelTitle: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#1f2937',
  },
  contactAvatarBig: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '32px',
    fontWeight: 700,
    color: '#fff',
    margin: '24px auto 12px',
    textTransform: 'uppercase',
  },
  contactName: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1f2937',
    textAlign: 'center',
    marginBottom: '4px',
  },
  contactPhone: {
    fontSize: '13px',
    color: '#6b7280',
    textAlign: 'center',
    fontFamily: 'monospace',
    marginBottom: '20px',
  },
  contactSection: {
    padding: '16px',
    borderTop: '8px solid #f0f2f5',
  },
  contactSectionTitle: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#00a884',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '12px',
  },
  contactRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
  },
  contactLabel: {
    fontSize: '13px',
    color: '#6b7280',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  contactValue: {
    fontSize: '13px',
    color: '#1f2937',
    fontWeight: 500,
    textAlign: 'right',
    maxWidth: '160px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  contactStatGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '8px',
    marginBottom: '4px',
  },
  contactStatCard: (bg) => ({
    textAlign: 'center',
    padding: '10px 6px',
    borderRadius: '8px',
    background: bg,
  }),
  contactStatNumber: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#1f2937',
    lineHeight: 1.2,
  },
  contactStatLabel: {
    fontSize: '10px',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    marginTop: '2px',
  },
  contactPacienteCard: {
    padding: '12px',
    background: '#f0f9ff',
    borderRadius: '8px',
    border: '1px solid #bae6fd',
  },
  contactPacienteName: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#0c4a6e',
    marginBottom: '6px',
  },
  contactPacienteInfo: {
    fontSize: '12px',
    color: '#0369a1',
    lineHeight: 1.6,
  },
};

// ─── Component ─────────────────────────────────────────────────────────────────

function WhatsAppConversas() {
  const { user } = useAuth();
  const { phone: phoneParam } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // State
  const [conversations, setConversations] = useState([]);
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [sending, setSending] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [conversationWindow, setConversationWindow] = useState(null);
  const [aiStatus, setAiStatus] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [captionInput, setCaptionInput] = useState('');
  const [lightboxImage, setLightboxImage] = useState(null);
  const [error, setError] = useState(null);
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const [windowCountdown, setWindowCountdown] = useState(null);
  const [receivedMessages, setReceivedMessages] = useState([]);
  const [contactPanel, setContactPanel] = useState(null);
  const [loadingContact, setLoadingContact] = useState(false);
  const [atendimento, setAtendimento] = useState(null);
  const [viewingHistorico, setViewingHistorico] = useState(false);

  // Filter & assignment state
  const [filterTab, setFilterTab] = useState('minhas');
  const [setores, setSetores] = useState([]);
  const [selectedSetor, setSelectedSetor] = useState('');
  const [showAssignDropdown, setShowAssignDropdown] = useState(false);
  const [assignSetorId, setAssignSetorId] = useState('');
  const [assignAtendenteId, setAssignAtendenteId] = useState('');
  const [assignUsuarios, setAssignUsuarios] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);

  // Paciente popup state
  const [pacienteInfo, setPacienteInfo] = useState(null);
  const [showPacientePopup, setShowPacientePopup] = useState(false);
  const [loadingPaciente, setLoadingPaciente] = useState(false);

  // Refs
  const messagesEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const audioChunksRef = useRef([]);
  const fileInputRef = useRef(null);
  const fileTypeRef = useRef(null);
  const textAreaRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const convPollIntervalRef = useRef(null);
  const messagesAreaRef = useRef(null);
  const socketRef = useRef(null);
  const selectedPhoneRef = useRef(null);

  // ─── Fetch Conversations ───────────────────────────────────────────────

  const fetchConversations = useCallback(async (showLoader) => {
    if (showLoader) setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterTab) params.set('filter', filterTab);
      if (selectedSetor) params.set('setor_id', selectedSetor);
      const res = await axios.get(`${API_URL}/whatsapp/conversas-unificadas?${params}`);
      setConversations(res.data || []);
    } catch (err) {
      console.error('Erro ao buscar conversas:', err);
      if (showLoader) setError('Erro ao carregar conversas');
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [filterTab, selectedSetor]);

  useEffect(() => {
    fetchConversations(true);
  }, [fetchConversations]);

  // Carregar setores do usuario
  useEffect(() => {
    const fetchSetores = async () => {
      try {
        const res = await axios.get(`${API_URL}/whatsapp/setores?meus=1`);
        setSetores(res.data || []);
      } catch (err) {
        console.error('Erro ao buscar setores:', err);
      }
    };
    fetchSetores();
  }, []);

  // Auto-refresh conversations every 60s (fallback, Socket.IO handles real-time)
  useEffect(() => {
    convPollIntervalRef.current = setInterval(() => {
      fetchConversations(false);
    }, 60000);
    return () => clearInterval(convPollIntervalRef.current);
  }, [fetchConversations]);

  // ─── Fetch Timeline ────────────────────────────────────────────────────

  const fetchTimeline = useCallback(async (phone, showLoader, atendimentoId) => {
    if (!phone) return;
    if (showLoader) setLoadingMessages(true);
    try {
      let url = `${API_URL}/whatsapp/conversas-unificadas/${encodeURIComponent(phone)}`;
      if (atendimentoId) url += `?atendimento_id=${atendimentoId}`;
      const res = await axios.get(url);
      setTimeline(res.data || []);
    } catch (err) {
      console.error('Erro ao buscar timeline:', err);
    } finally {
      if (showLoader) setLoadingMessages(false);
    }
  }, []);

  // ─── Fetch Received Messages (for media) ───────────────────────────────

  const fetchReceivedMessages = useCallback(async (phone) => {
    if (!phone) return;
    try {
      const res = await axios.get(`${API_URL}/whatsapp/mensagens-recebidas?from_phone=${encodeURIComponent(phone)}`);
      setReceivedMessages(res.data || []);
    } catch (err) {
      console.error('Erro ao buscar mensagens recebidas:', err);
    }
  }, []);

  // ─── Fetch Conversation Window ─────────────────────────────────────────

  const fetchConversationWindow = useCallback(async (phone) => {
    if (!phone) return;
    try {
      const res = await axios.get(`${API_URL}/whatsapp/conversation-window/${encodeURIComponent(phone)}`);
      setConversationWindow(res.data || { open: false });
    } catch (err) {
      console.error('Erro ao buscar janela de conversa:', err);
      setConversationWindow({ open: false });
    }
  }, []);

  // ─── Fetch Contact Details ──────────────────────────────────────────────

  const fetchContactDetails = useCallback(async (phone) => {
    if (!phone) return;
    setLoadingContact(true);
    try {
      const res = await axios.get(`${API_URL}/whatsapp/contato/${encodeURIComponent(phone)}`);
      setContactPanel(res.data || null);
    } catch (err) {
      console.error('Erro ao buscar detalhes do contato:', err);
      setContactPanel(null);
    } finally {
      setLoadingContact(false);
    }
  }, []);

  // ─── Fetch Atendimento ────────────────────────────────────────────────

  const fetchAtendimento = useCallback(async (phone) => {
    if (!phone) return;
    try {
      const res = await axios.get(`${API_URL}/whatsapp/atendimento/${encodeURIComponent(phone)}`);
      setAtendimento(res.data || null);
    } catch {
      setAtendimento(null);
    }
  }, []);

  // ─── Select Conversation ───────────────────────────────────────────────

  const selectConversation = useCallback(async (phone) => {
    setSelectedPhone(phone);
    const encoded = encodeURIComponent(phone);
    const target = `/whatsapp-conversas/${encoded}`;
    lastNavKey.current = `${encoded}__`;
    if (window.location.pathname !== target) {
      navigate(target, { replace: true });
    }
    setTimeline([]);
    setMediaPreview(null);
    setMessageInput('');
    setShowAttachMenu(false);
    setError(null);
    setContactPanel(null);
    setMobileShowChat(true);
    setAiStatus(null);
    setConversationWindow(null);
    setAtendimento(null);
    setViewingHistorico(false);
    setShowPacientePopup(false);
    setShowAssignDropdown(false);

    await Promise.all([
      fetchTimeline(phone, true),
      fetchConversationWindow(phone),
      fetchReceivedMessages(phone),
      fetchAtendimento(phone),
      fetchAiStatus(),
    ]);
  }, [fetchTimeline, fetchConversationWindow, fetchReceivedMessages, fetchAtendimento]);

  // Auto-selecionar conversa quando abre via URL com phone
  // Usa ts (timestamp) do query param para forçar re-fetch do mesmo telefone (ex: vindo do Histórico)
  // Usa atendimento_id para carregar mensagens de um atendimento específico
  const lastNavKey = useRef(null);
  useEffect(() => {
    if (!phoneParam) return;
    const ts = searchParams.get('ts') || '';
    const atendimentoId = searchParams.get('atendimento_id') || '';
    const navKey = `${phoneParam}__${ts}`;
    if (navKey === lastNavKey.current) return;
    lastNavKey.current = navKey;
    const phone = decodeURIComponent(phoneParam);
    setSelectedPhone(phone);
    setTimeline([]);
    setMobileShowChat(true);
    setConversationWindow(null);
    setAtendimento(null);
    setViewingHistorico(!!atendimentoId);
    fetchTimeline(phone, true, atendimentoId || null);
    fetchConversationWindow(phone);
    fetchReceivedMessages(phone);
    fetchAtendimento(phone);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneParam, searchParams]);

  // Poll for new messages every 60s (fallback, Socket.IO handles real-time)
  useEffect(() => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    if (!selectedPhone) return;

    pollIntervalRef.current = setInterval(() => {
      fetchTimeline(selectedPhone, false);
      fetchConversationWindow(selectedPhone);
    }, 60000);

    return () => clearInterval(pollIntervalRef.current);
  }, [selectedPhone, fetchTimeline, fetchConversationWindow]);

  // ─── Paciente Info (para popup) ──────────────────────────────────────

  const fetchPacienteInfo = useCallback(async (phone) => {
    setLoadingPaciente(true);
    try {
      const res = await axios.get(`${API_URL}/whatsapp/atendimento/${encodeURIComponent(phone)}/paciente-info`);
      setPacienteInfo(res.data);
      setShowPacientePopup(true);
    } catch (err) {
      console.error('Erro ao buscar paciente:', err);
      setError('Nao foi possivel buscar dados do paciente');
    } finally {
      setLoadingPaciente(false);
    }
  }, []);

  // ─── Atribuir Conversa ─────────────────────────────────────────────────

  const handleAtribuir = async () => {
    if (!selectedPhone) return;
    setAssignLoading(true);
    try {
      await axios.post(`${API_URL}/whatsapp/atendimento/${encodeURIComponent(selectedPhone)}/atribuir`, {
        atendente_id: assignAtendenteId ? parseInt(assignAtendenteId) : null,
        setor_id: assignSetorId ? parseInt(assignSetorId) : null,
      });
      setShowAssignDropdown(false);
      fetchAtendimento(selectedPhone);
      fetchConversations(false);
    } catch (err) {
      console.error('Erro ao atribuir:', err);
      setError(err.response?.data?.error || 'Erro ao atribuir conversa');
    } finally {
      setAssignLoading(false);
    }
  };

  const loadAssignUsuarios = async () => {
    try {
      const res = await axios.get(`${API_URL}/auth/users`);
      setAssignUsuarios((res.data || []).filter(u => u.ativo === 1));
    } catch (err) {
      console.error('Erro ao buscar usuarios:', err);
    }
  };

  // ─── Enviar Mensagem Pronta (do popup paciente) ─────────────────────

  const enviarMensagemPronta = (texto) => {
    setMessageInput(texto);
    setShowPacientePopup(false);
    // Focar no textarea
    setTimeout(() => textAreaRef.current?.focus(), 100);
  };

  // ─── Enviar PDF do Laudo no Chat ──────────────────────────────────────

  const enviarPdfNoChat = async (local, protocolo, pacienteNome) => {
    if (!selectedPhone) return;
    setSending(true);
    setError(null);
    try {
      // 1. Baixar PDF do endpoint de laudos
      const pdfRes = await axios.get(
        `${API_URL}/laudos/download/${encodeURIComponent(local.trim())}/${protocolo}`,
        { responseType: 'arraybuffer' }
      );

      // 2. Converter para base64
      const base64 = btoa(
        new Uint8Array(pdfRes.data).reduce((data, byte) => data + String.fromCharCode(byte), '')
      );

      // 3. Enviar via send-media como document
      const nomeArquivo = `laudo_${local.trim()}${protocolo}.pdf`;
      await axios.post(`${API_URL}/whatsapp/chat/send-media`, {
        phone: selectedPhone,
        media_type: 'document',
        media_base64: base64,
        media_mimetype: 'application/pdf',
        filename: nomeArquivo,
        caption: `Resultado de exames - Protocolo ${protocolo}${pacienteNome ? ` - ${pacienteNome}` : ''}`,
      });

      // 4. Atualizar timeline
      fetchTimeline(selectedPhone, false);
      fetchConversations(false);
      setShowPacientePopup(false);
    } catch (err) {
      console.error('Erro ao enviar PDF:', err);
      setError(err.response?.data?.error || 'Erro ao enviar PDF do laudo');
    } finally {
      setSending(false);
    }
  };

  // ─── Socket.IO Connection ────────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const socket = io(SOCKET_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
    });

    socket.on('connect', () => {
      console.log('[Socket.IO] Conectado');
    });

    socket.on('disconnect', (reason) => {
      console.log('[Socket.IO] Desconectado:', reason);
    });

    socket.on('connect_error', (err) => {
      console.warn('[Socket.IO] Erro de conexão:', err.message);
    });

    // Quando lista de conversas muda (nova msg em qualquer conversa)
    socket.on('whatsapp:conversation-updated', (data) => {
      fetchConversations(false);
      // Atualizar atendimento se a conversa selecionada foi atualizada
      const currentPhone = selectedPhoneRef.current;
      if (currentPhone && data && data.phone === currentPhone) {
        fetchAtendimento(currentPhone);
      }
    });

    // Quando chega mensagem na conversa aberta
    socket.on('whatsapp:message', (data) => {
      const currentPhone = selectedPhoneRef.current;
      if (!currentPhone || data.phone !== currentPhone) return;

      // Fazer refresh da timeline para pegar dados completos do banco
      fetchTimeline(currentPhone, false);
      fetchConversationWindow(currentPhone);
      fetchReceivedMessages(currentPhone);
    });

    socketRef.current = socket;

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [fetchConversations, fetchTimeline, fetchConversationWindow, fetchReceivedMessages, fetchAtendimento]);

  // Reload ao voltar para a aba ou reconectar internet
  useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden) {
        fetchConversations(false);
        const phone = selectedPhoneRef.current;
        if (phone) {
          fetchTimeline(phone, false);
          fetchConversationWindow(phone);
        }
      }
    };

    const handleOnline = () => {
      fetchConversations(false);
      const phone = selectedPhoneRef.current;
      if (phone) {
        fetchTimeline(phone, false);
        fetchConversationWindow(phone);
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('online', handleOnline);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('online', handleOnline);
    };
  }, [fetchConversations, fetchTimeline, fetchConversationWindow]);

  // ─── Socket.IO room management por conversa ──────────────────────────
  useEffect(() => {
    selectedPhoneRef.current = selectedPhone;
    const socket = socketRef.current;
    if (!socket) return;

    if (selectedPhone) {
      socket.emit('join:phone', selectedPhone);
    }

    return () => {
      if (selectedPhone) {
        socket.emit('leave:phone', selectedPhone);
      }
    };
  }, [selectedPhone]);

  // ─── Auto-scroll to bottom ─────────────────────────────────────────────

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [timeline]);

  // ─── Conversation Window Countdown ─────────────────────────────────────

  useEffect(() => {
    if (!conversationWindow?.open || !conversationWindow?.expires_at) {
      setWindowCountdown(null);
      return;
    }

    const updateCountdown = () => {
      const remaining = formatCountdown(conversationWindow.expires_at);
      setWindowCountdown(remaining);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 60000);
    return () => clearInterval(interval);
  }, [conversationWindow]);

  // ─── Send Text Message (optimistic) ────────────────────────────────────

  const handleSendText = () => {
    const text = messageInput.trim();
    if (!text || !selectedPhone || sending) return;

    // Limpar input imediatamente
    setMessageInput('');
    if (textAreaRef.current) {
      textAreaRef.current.style.height = '42px';
    }
    setError(null);

    // Adicionar mensagem otimista na timeline
    const optimisticMsg = {
      tipo: 'envio',
      id: `opt-${Date.now()}`,
      data: new Date().toISOString(),
      nome: null,
      mensagem: text,
      tipo_msg: 'chat',
      extra: 'enviando...',
      sender: null,
      _optimistic: true,
    };
    setTimeline(prev => [...prev, optimisticMsg]);

    // Enviar em background (fire-and-forget)
    setSending(true);
    axios.post(`${API_URL}/whatsapp/chat/send-text`, {
      phone: selectedPhone,
      message: text,
    })
      .then(() => {
        // Refresh timeline para pegar dados reais do banco
        fetchTimeline(selectedPhone, false);
        fetchConversationWindow(selectedPhone);
        fetchAtendimento(selectedPhone);
        fetchConversations(false);
      })
      .catch((err) => {
        console.error('Erro ao enviar mensagem:', err);
        setError(err.response?.data?.error || 'Erro ao enviar mensagem');
        // Remover msg otimista em caso de erro
        setTimeline(prev => prev.filter(m => m.id !== optimisticMsg.id));
      })
      .finally(() => {
        setSending(false);
        setTimeout(() => textAreaRef.current?.focus(), 0);
      });
  };

  // ─── Handle Keyboard ──────────────────────────────────────────────────

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendText();
    }
  };

  // ─── Auto-resize textarea ──────────────────────────────────────────────

  const handleTextAreaChange = (e) => {
    setMessageInput(e.target.value);
    const el = e.target;
    el.style.height = '42px';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  };

  // ─── Toggle AI ─────────────────────────────────────────────────────────

  const fetchAiStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/whatsapp/chat/ai-status`);
      setAiStatus(res.data);
    } catch (err) {
      console.error('Erro ao buscar status IA:', err);
    }
  };

  const handleToggleAI = async () => {
    try {
      const res = await axios.post(`${API_URL}/whatsapp/chat/toggle-ai`);
      setAiStatus(res.data);
    } catch (err) {
      console.error('Erro ao alternar IA:', err);
      setError('Erro ao alternar IA');
    }
  };

  // ─── Toggle IA Pausada (por atendimento) ────────────────────────────

  const handleToggleIA = async () => {
    if (!selectedPhone || !atendimento || atendimento.status !== 'aberto') return;
    try {
      const res = await axios.post(`${API_URL}/whatsapp/atendimento/${encodeURIComponent(selectedPhone)}/toggle-ia`);
      if (res.data.success) {
        fetchAtendimento(selectedPhone);
        fetchConversations(false);
      }
    } catch (err) {
      console.error('Erro ao alternar IA:', err);
      setError('Erro ao alternar pausa da IA');
    }
  };

  // ─── Fechar Atendimento ──────────────────────────────────────────────

  const handleFecharAtendimento = async () => {
    if (!selectedPhone || !atendimento || atendimento.status !== 'aberto') return;
    if (!window.confirm(`Encerrar atendimento ${atendimento.protocolo}?`)) return;
    try {
      await axios.post(`${API_URL}/whatsapp/atendimento/${encodeURIComponent(selectedPhone)}/fechar`);
      fetchAtendimento(selectedPhone);
      fetchTimeline(selectedPhone, false);
      fetchConversations(false);
    } catch (err) {
      console.error('Erro ao fechar atendimento:', err);
      setError('Erro ao encerrar atendimento');
    }
  };

  // ─── Audio Recording ──────────────────────────────────────────────────

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')
          ? 'audio/ogg;codecs=opus'
          : 'audio/webm';

      const recorder = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());

        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        const reader = new FileReader();
        reader.onloadend = async () => {
          const base64 = reader.result.split(',')[1];
          setSending(true);
          try {
            await axios.post(`${API_URL}/whatsapp/chat/send-media`, {
              phone: selectedPhone,
              media_type: 'audio',
              media_base64: base64,
              media_mimetype: mimeType.split(';')[0],
              filename: `audio_${Date.now()}.${mimeType.includes('ogg') ? 'ogg' : 'webm'}`,
            });
            await fetchTimeline(selectedPhone, false);
          } catch (err) {
            console.error('Erro ao enviar audio:', err);
            setError('Erro ao enviar audio');
          } finally {
            setSending(false);
          }
        };
        reader.readAsDataURL(blob);
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      recordingTimerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (err) {
      console.error('Erro ao acessar microfone:', err);
      setError('Erro ao acessar microfone. Verifique as permissoes do navegador.');
    }
  };

  const stopRecording = (cancel) => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    setIsRecording(false);
    setRecordingTime(0);

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      if (cancel) {
        mediaRecorderRef.current.stream.getTracks().forEach((t) => t.stop());
        mediaRecorderRef.current.ondataavailable = null;
        mediaRecorderRef.current.onstop = null;
        mediaRecorderRef.current.stop();
        mediaRecorderRef.current = null;
      } else {
        mediaRecorderRef.current.stop();
      }
    }
  };

  const handleMicClick = () => {
    if (isRecording) {
      stopRecording(false);
    } else {
      startRecording();
    }
  };

  // ─── File Attachment ──────────────────────────────────────────────────

  const handleAttachClick = (type) => {
    setShowAttachMenu(false);
    fileTypeRef.current = type;

    const accepts = {
      image: 'image/*',
      audio: 'audio/*',
      video: 'video/*',
      document: '.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip,.rar',
    };

    if (fileInputRef.current) {
      fileInputRef.current.accept = accepts[type] || '*/*';
      fileInputRef.current.click();
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const type = fileTypeRef.current || 'document';
    const reader = new FileReader();

    reader.onloadend = () => {
      const base64 = reader.result.split(',')[1];
      let preview = null;
      if (type === 'image') {
        preview = reader.result;
      }

      setMediaPreview({
        file,
        type,
        base64,
        preview,
        mimetype: file.type,
        filename: file.name,
      });
      setCaptionInput('');
    };

    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleSendMedia = async () => {
    if (!mediaPreview || !selectedPhone || sending) return;

    setSending(true);
    setError(null);
    try {
      await axios.post(`${API_URL}/whatsapp/chat/send-media`, {
        phone: selectedPhone,
        media_type: mediaPreview.type,
        media_base64: mediaPreview.base64,
        media_mimetype: mediaPreview.mimetype,
        filename: mediaPreview.filename,
        caption: captionInput.trim() || undefined,
      });
      setMediaPreview(null);
      setCaptionInput('');
      await fetchTimeline(selectedPhone, false);
    } catch (err) {
      console.error('Erro ao enviar midia:', err);
      setError(err.response?.data?.error || 'Erro ao enviar midia');
    } finally {
      setSending(false);
    }
  };

  // ─── Close attach menu on outside click ────────────────────────────────

  useEffect(() => {
    if (!showAttachMenu) return;
    const handler = (e) => {
      if (!e.target.closest('[data-attach-menu]')) {
        setShowAttachMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showAttachMenu]);

  // ─── Build media URL for received messages ─────────────────────────────

  const getMediaForMessage = useCallback(
    (msg) => {
      if (msg.tipo !== 'recebida' && msg.tipo !== 'user') return null;

      const received = receivedMessages.find((rm) => {
        if (rm.media_id && msg.extra) {
          try {
            const extra = typeof msg.extra === 'string' ? JSON.parse(msg.extra) : msg.extra;
            if (extra.media_id === rm.media_id) return true;
          } catch {}
        }
        if (rm.id === msg.id) return true;
        return false;
      });

      if (!received || !received.media_id) return null;

      return {
        media_id: received.media_id,
        type: received.type || 'document',
        url: `${API_URL}/whatsapp/chat/media/${received.media_id}`,
        mimetype: received.mimetype || '',
      };
    },
    [receivedMessages]
  );

  // ─── Filter Conversations ─────────────────────────────────────────────

  const filteredConversations = conversations.filter((c) => {
    if (!searchFilter) return true;
    const q = searchFilter.toLowerCase();
    return (
      (c.phone || '').toLowerCase().includes(q) || (c.nome || '').toLowerCase().includes(q)
    );
  });

  // ─── Selected conversation info ────────────────────────────────────────

  const selectedConvInfo = conversations.find((c) => c.phone === selectedPhone);

  // ─── Group messages by date ────────────────────────────────────────────

  const groupedMessages = [];
  let lastDateLabel = null;

  timeline.forEach((msg, i) => {
    const dateLabel = formatDateLabel(msg.data);
    if (dateLabel && dateLabel !== lastDateLabel) {
      groupedMessages.push({ type: 'date', label: dateLabel, key: `date-${i}` });
      lastDateLabel = dateLabel;
    }
    groupedMessages.push({ type: 'message', msg, key: `${msg.tipo}-${msg.id}-${i}` });
  });

  // ─── Render Media in Message ───────────────────────────────────────────

  const renderMessageMedia = (msg) => {
    // Tentar extrair media_id do campo extra (inline na timeline)
    let mediaId = null;
    let type = (msg.tipo_msg || '').toLowerCase();

    if (msg.extra) {
      try {
        const extra = typeof msg.extra === 'string' ? JSON.parse(msg.extra) : msg.extra;
        if (extra.media_id) mediaId = extra.media_id;
      } catch {}
    }

    // Fallback: buscar nas receivedMessages
    if (!mediaId) {
      const media = getMediaForMessage(msg);
      if (!media) return null;
      mediaId = media.media_id;
      type = media.type || type;
    }

    if (!mediaId) return null;

    const token = localStorage.getItem('token');
    const url = `${API_URL}/whatsapp/chat/media/${mediaId}${token ? `?token=${token}` : ''}`;

    if (type === 'image' || type === 'sticker') {
      return (
        <img
          src={url}
          alt="Imagem"
          style={styles.mediaImage}
          onClick={() => setLightboxImage(url)}
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      );
    }
    if (type === 'audio' || type === 'ptt') {
      return <audio controls src={url} style={styles.mediaAudio} preload="metadata" />;
    }
    if (type === 'video') {
      return (
        <video controls src={url} style={styles.mediaVideo} preload="metadata" />
      );
    }
    if (type === 'document' || type.includes('pdf')) {
      return (
        <a href={url} target="_blank" rel="noopener noreferrer" style={styles.mediaDocLink}>
          <span style={{ fontSize: '20px' }}>&#128196;</span>
          <span>Baixar arquivo</span>
        </a>
      );
    }
    return null;
  };

  // Check if a message's extra contains media info for envio/assistant types
  const renderEnvioMedia = (msg) => {
    if (msg.tipo !== 'envio') return null;
    if (!msg.tipo_msg) return null;

    // For envio messages that are media type
    const mediaTypes = ['image', 'audio', 'video', 'document', 'pdf'];
    const msgType = (msg.tipo_msg || '').toLowerCase();
    if (!mediaTypes.some(t => msgType.includes(t))) return null;

    // Try to get media info from extra
    let extra = null;
    if (msg.extra) {
      try {
        extra = typeof msg.extra === 'string' ? JSON.parse(msg.extra) : msg.extra;
      } catch {}
    }

    if (!extra) return null;

    if (extra.media_id) {
      const tkn = localStorage.getItem('token');
      const url = `${API_URL}/whatsapp/chat/media/${extra.media_id}${tkn ? `?token=${tkn}` : ''}`;
      if (msgType.includes('image')) {
        return (
          <img
            src={url}
            alt="Imagem enviada"
            style={styles.mediaImage}
            onClick={() => setLightboxImage(url)}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        );
      }
      if (msgType.includes('audio')) {
        return <audio controls src={url} style={styles.mediaAudio} preload="metadata" />;
      }
      if (msgType.includes('video')) {
        return <video controls src={url} style={styles.mediaVideo} preload="metadata" />;
      }
      // document, pdf_ia, pdf, etc.
      return (
        <a href={url} target="_blank" rel="noopener noreferrer" style={styles.mediaDocLink}>
          <span style={{ fontSize: '20px' }}>&#128196;</span>
          <span>Baixar arquivo</span>
        </a>
      );
    }

    return null;
  };

  // ─── Render ────────────────────────────────────────────────────────────

  // Inject keyframes animation for the recording dot pulse
  useEffect(() => {
    const styleId = 'whatsapp-conversas-keyframes';
    if (document.getElementById(styleId)) return;
    const styleEl = document.createElement('style');
    styleEl.id = styleId;
    styleEl.textContent = `
      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.3; }
      }
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      @media (max-width: 768px) {
        .wa-left-panel {
          width: 100% !important;
          min-width: 100% !important;
          border-right: none !important;
        }
        .wa-chat-active .wa-left-panel {
          display: none !important;
        }
        .wa-chat-active .wa-right-panel {
          width: 100% !important;
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          z-index: 10 !important;
        }
        .wa-mobile-back {
          display: flex !important;
        }
        .wa-messages-area {
          padding: 8px 10px !important;
        }
        .wa-message-bubble {
          max-width: 90% !important;
        }
        .wa-contact-panel {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          width: 100% !important;
          min-width: 100% !important;
          z-index: 20 !important;
        }
        .wa-header-actions {
          gap: 4px !important;
        }
        .wa-header-actions .wa-header-btn-text {
          display: none !important;
        }
        .wa-header-actions .wa-window-badge {
          font-size: 10px !important;
          padding: 2px 6px !important;
        }
      }
    `;
    document.head.appendChild(styleEl);
    return () => {
      const el = document.getElementById(styleId);
      if (el) el.remove();
    };
  }, []);

  // Hidden file input
  const fileInput = (
    <input
      ref={fileInputRef}
      type="file"
      style={{ display: 'none' }}
      onChange={handleFileSelect}
    />
  );

  // Lightbox
  const lightbox = lightboxImage ? (
    <div style={styles.lightboxOverlay} onClick={() => setLightboxImage(null)}>
      <button style={styles.lightboxClose} onClick={() => setLightboxImage(null)}>
        &#10005;
      </button>
      <img src={lightboxImage} alt="Preview" style={styles.lightboxImage} />
    </div>
  ) : null;

  // Loading state
  if (loading) {
    return (
      <div style={styles.wrapper}>
        <div style={styles.container}>
          <div style={{ ...styles.loadingOverlay, width: '100%' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={styles.spinner} />
              <p style={{ color: '#667781', marginTop: '16px', fontSize: '14px' }}>
                Carregando conversas...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.wrapper}>
      {fileInput}
      {lightbox}

      <div style={styles.container} className={mobileShowChat && selectedPhone ? 'wa-chat-active' : ''}>
        {/* ─── LEFT PANEL ─── */}
        <div
          className="wa-left-panel"
          style={styles.leftPanel}
        >
          {/* Header */}
          <div style={styles.leftHeader}>
            <div style={styles.leftHeaderTitle}>
              <span style={styles.pageIcon}>&#128172;</span>
              <div>
                <h1 style={styles.pageTitle}>Conversas</h1>
              </div>
            </div>
            <button
              onClick={() => fetchConversations(false)}
              style={{ ...styles.headerBtn, padding: '4px 8px', fontSize: '12px' }}
              title="Atualizar lista"
            >
              &#8635;
            </button>
          </div>

          {/* Search */}
          <div style={styles.searchContainer}>
            <div style={styles.searchWrapper}>
              <span style={styles.searchIcon}>&#128269;</span>
              <input
                type="text"
                placeholder="Pesquisar por nome ou telefone"
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                style={styles.searchInput}
              />
            </div>
          </div>

          {/* Filter Chips */}
          <div style={{ padding: '4px 12px 8px', display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center', background: '#f0f2f5' }}>
            {[
              { key: 'minhas', label: 'Minhas' },
              { key: 'nao_atribuidas', label: 'Nao atribuidas' },
              ...(user?.role === 'admin' ? [{ key: 'todas', label: 'Todas' }] : []),
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setFilterTab(f.key)}
                style={{
                  padding: '4px 12px', borderRadius: '16px', fontSize: '11px', fontWeight: 600,
                  border: '1px solid', cursor: 'pointer', transition: 'all 0.15s',
                  background: filterTab === f.key ? '#1d4ed8' : '#fff',
                  color: filterTab === f.key ? '#fff' : '#374151',
                  borderColor: filterTab === f.key ? '#1d4ed8' : '#d1d5db',
                }}
              >
                {f.label}
              </button>
            ))}
            {setores.length > 1 && (
              <select
                value={selectedSetor}
                onChange={(e) => setSelectedSetor(e.target.value)}
                style={{
                  padding: '4px 8px', borderRadius: '8px', fontSize: '11px', border: '1px solid #d1d5db',
                  background: '#fff', cursor: 'pointer', color: '#374151',
                }}
              >
                <option value="">Todos setores</option>
                {setores.map(s => (
                  <option key={s.id} value={s.id}>{s.nome}</option>
                ))}
              </select>
            )}
          </div>

          {/* Conversation List */}
          <div style={styles.conversationList}>
            {filteredConversations.length === 0 ? (
              <div style={{ padding: '40px 20px', textAlign: 'center', color: '#9ca3af' }}>
                <div style={{ fontSize: '40px', marginBottom: '8px', opacity: 0.5 }}>&#128172;</div>
                <p style={{ fontSize: '13px', margin: 0 }}>
                  {searchFilter ? 'Nenhuma conversa encontrada' : 'Nenhuma conversa disponivel'}
                </p>
              </div>
            ) : (
              filteredConversations.map((conv, i) => (
                <div
                  key={conv.phone || i}
                  style={styles.conversationItem(selectedPhone === conv.phone)}
                  onClick={() => selectConversation(conv.phone)}
                  onMouseEnter={(e) => {
                    if (selectedPhone !== conv.phone) {
                      e.currentTarget.style.background = '#f9fafb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedPhone !== conv.phone) {
                      e.currentTarget.style.background = '#fff';
                    }
                  }}
                >
                  <div style={styles.avatar}>
                    {(conv.nome || conv.phone || '?')[0]}
                  </div>
                  <div style={styles.convInfo}>
                    <div style={styles.convNameRow}>
                      <span style={styles.convName}>
                        {conv.nome || conv.phone || 'Desconhecido'}
                      </span>
                      <span style={styles.convTime}>
                        <span style={{ marginRight: '3px', fontSize: '10px', opacity: 0.7 }}>&#128336;</span>
                        {formatRelativeTime(conv.ultimo_contato)}
                      </span>
                    </div>
                    {conv.nome && (
                      <div style={styles.convPhone}>{conv.phone}</div>
                    )}
                    <div style={styles.convLastMsg}>
                      {truncateText(conv.ultima_mensagem, 45) || 'Sem mensagens'}
                    </div>
                    <div style={styles.convBadges}>
                      {conv.total_recebidas > 0 && (
                        <span style={styles.badge('#dcfce7', '#166534')} title={`${conv.total_recebidas} recebidas`}>
                          &#8600; {conv.total_recebidas}
                        </span>
                      )}
                      {conv.total_envios > 0 && (
                        <span style={styles.badge('#dbeafe', '#1e40af')} title={`${conv.total_envios} enviadas`}>
                          &#8599; {conv.total_envios}
                        </span>
                      )}
                      {conv.total_ia > 0 && (
                        <span style={styles.badge('#ede9fe', '#5b21b6')} title={`${conv.total_ia} IA`}>
                          &#129302; {conv.total_ia}
                        </span>
                      )}
                      {conv.atendimento_protocolo && (
                        <span
                          style={styles.badge(
                            conv.atendimento_status === 'aberto' ? '#dcfce7' : '#f3f4f6',
                            conv.atendimento_status === 'aberto' ? '#166534' : '#9ca3af'
                          )}
                          title={`Atendimento ${conv.atendimento_status}`}
                        >
                          {conv.atendimento_protocolo}
                        </span>
                      )}
                      {conv.atendente_nome && conv.atendimento_status === 'aberto' && (
                        <span
                          style={styles.badge('#e0f2f1', '#00695c')}
                          title={`Atendente: ${conv.atendente_nome}`}
                        >
                          {conv.atendente_nome.split(' ')[0]}
                        </span>
                      )}
                      {conv.setor_nome && conv.atendimento_status === 'aberto' && (
                        <span
                          style={styles.badge('#e8eaf6', '#283593')}
                          title={`Setor: ${conv.setor_nome}`}
                        >
                          {conv.setor_nome}
                        </span>
                      )}
                      {!conv.atendente_id && conv.atendimento_status === 'aberto' && (
                        <span
                          style={styles.badge('#fef3c7', '#92400e')}
                          title="Nao atribuida"
                        >
                          Nao atribuida
                        </span>
                      )}
                      {conv.ia_pausada && conv.atendimento_status === 'aberto' && (
                        <span
                          style={styles.badge('#fef3c7', '#92400e')}
                          title="IA pausada - Atendente assumiu"
                        >
                          IA pausada
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* ─── RIGHT PANEL ─── */}
        <div
          className="wa-right-panel"
          style={{
            ...styles.rightPanel,
            ...((!mobileShowChat || !selectedPhone) ? {} : {}),
          }}
        >
          {!selectedPhone ? (
            /* Empty State */
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>&#128172;</div>
              <div style={styles.emptyTitle}>WhatsApp Conversas</div>
              <div style={styles.emptySubtitle}>
                Selecione uma conversa para visualizar e enviar mensagens
              </div>
            </div>
          ) : (
            <>
              {/* Chat Header */}
              <div style={styles.chatHeader}>
                <button
                  className="wa-mobile-back"
                  style={styles.mobileBackBtn}
                  onClick={() => {
                    setMobileShowChat(false);
                    setSelectedPhone(null);
                  }}
                >
                  &#8592;
                </button>
                <div
                  style={{ ...styles.avatar, cursor: 'pointer' }}
                  onClick={() => fetchContactDetails(selectedPhone)}
                  title="Ver detalhes do contato"
                >
                  {(selectedConvInfo?.nome || selectedPhone || '?')[0].toUpperCase()}
                </div>
                <div
                  style={{ ...styles.chatHeaderInfo, cursor: 'pointer' }}
                  onClick={() => fetchContactDetails(selectedPhone)}
                  title="Ver detalhes do contato"
                >
                  <div style={styles.chatHeaderName}>
                    {selectedConvInfo?.nome || selectedPhone}
                  </div>
                  <div style={styles.chatHeaderPhone}>
                    {selectedConvInfo?.nome ? selectedPhone : ''}
                  </div>
                </div>
                <div className="wa-header-actions" style={styles.chatHeaderActions}>
                  {/* Atendimento protocolo + botão encerrar */}
                  {atendimento && (
                    <>
                      <span style={{
                        padding: '3px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600,
                        background: atendimento.status === 'aberto' ? '#dcfce7' : '#f3f4f6',
                        color: atendimento.status === 'aberto' ? '#166534' : '#9ca3af',
                        border: `1px solid ${atendimento.status === 'aberto' ? '#bbf7d0' : '#e5e7eb'}`,
                      }}>
                        {atendimento.protocolo}
                      </span>
                      {atendimento.status === 'aberto' && (
                        <button
                          style={{ ...styles.headerBtn, borderColor: '#fca5a5', color: '#dc2626', fontSize: '12px' }}
                          onClick={handleFecharAtendimento}
                          title="Encerrar atendimento"
                        >
                          Encerrar
                        </button>
                      )}
                    </>
                  )}

                  {/* Historico de atendimentos anteriores */}
                  <button
                    style={{ ...styles.headerBtn, fontSize: '12px' }}
                    onClick={() => window.open(`/whatsapp-historico?search=${encodeURIComponent(selectedPhone)}`, '_blank')}
                    title="Ver atendimentos anteriores deste contato"
                  >
                    <span style={{ fontSize: '14px' }}>&#128203;</span>
                    <span className="wa-header-btn-text">Historico</span>
                  </button>

                  {/* Paciente Popup */}
                  <button
                    style={{ ...styles.headerBtn, fontSize: '12px' }}
                    onClick={() => fetchPacienteInfo(selectedPhone)}
                    title="Ver dados do paciente"
                    disabled={loadingPaciente}
                  >
                    <span style={{ fontSize: '14px' }}>&#128100;</span>
                    <span className="wa-header-btn-text">Paciente</span>
                  </button>

                  {/* Atribuir Conversa */}
                  {atendimento && atendimento.status === 'aberto' && (
                    <div style={{ position: 'relative' }}>
                      <button
                        style={{
                          ...styles.headerBtn,
                          fontSize: '12px',
                          borderColor: selectedConvInfo?.atendente_id ? '#a7f3d0' : '#fde68a',
                          background: selectedConvInfo?.atendente_id ? '#f0fdf4' : '#fffbeb',
                        }}
                        onClick={() => {
                          setShowAssignDropdown(!showAssignDropdown);
                          setAssignSetorId(atendimento.setor_id || '');
                          setAssignAtendenteId(atendimento.atendente_id || '');
                          loadAssignUsuarios();
                        }}
                        title="Atribuir conversa"
                      >
                        <span style={{ fontSize: '13px' }}>&#9997;</span>
                        <span className="wa-header-btn-text">
                          {selectedConvInfo?.atendente_nome ? selectedConvInfo.atendente_nome.split(' ')[0] : 'Atribuir'}
                        </span>
                      </button>
                      {showAssignDropdown && (
                        <div style={{
                          position: 'absolute', top: '100%', right: 0, zIndex: 100,
                          background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)', padding: '12px', width: '240px',
                          marginTop: '4px',
                        }}>
                          <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#1f2937' }}>
                            Atribuir conversa
                          </div>
                          <div style={{ marginBottom: '8px' }}>
                            <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: 600 }}>Setor</label>
                            <select
                              value={assignSetorId}
                              onChange={(e) => {
                                setAssignSetorId(e.target.value);
                              }}
                              style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '12px', marginTop: '2px' }}
                            >
                              <option value="">Sem setor</option>
                              {setores.map(s => (
                                <option key={s.id} value={s.id}>{s.nome}</option>
                              ))}
                            </select>
                          </div>
                          <div style={{ marginBottom: '10px' }}>
                            <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: 600 }}>Atendente</label>
                            <select
                              value={assignAtendenteId}
                              onChange={(e) => setAssignAtendenteId(e.target.value)}
                              style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '12px', marginTop: '2px' }}
                            >
                              <option value="">Nenhum</option>
                              {assignUsuarios.map(u => (
                                <option key={u.id} value={u.id}>{u.nome}</option>
                              ))}
                            </select>
                          </div>
                          <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                            <button
                              onClick={() => setShowAssignDropdown(false)}
                              style={{ padding: '5px 12px', borderRadius: '6px', border: '1px solid #d1d5db', background: '#fff', fontSize: '12px', cursor: 'pointer' }}
                            >
                              Cancelar
                            </button>
                            <button
                              onClick={handleAtribuir}
                              disabled={assignLoading}
                              style={{ padding: '5px 12px', borderRadius: '6px', border: 'none', background: '#1d4ed8', color: '#fff', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                            >
                              {assignLoading ? '...' : 'OK'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Conversation Window */}
                  {conversationWindow && (
                    <span className="wa-window-badge" style={styles.windowBadge(conversationWindow.open && windowCountdown)}>
                      {conversationWindow.open && windowCountdown
                        ? `Janela: ${windowCountdown}`
                        : 'Janela fechada'}
                    </span>
                  )}

                  {/* Toggle IA Pausada (por atendimento) */}
                  {atendimento && atendimento.status === 'aberto' && (
                    <button
                      style={{
                        ...styles.headerBtn,
                        borderColor: atendimento.ia_pausada ? '#86efac' : '#fde68a',
                        color: atendimento.ia_pausada ? '#166534' : '#92400e',
                        background: atendimento.ia_pausada ? '#f0fdf4' : '#fffbeb',
                        fontWeight: 600,
                        fontSize: '12px',
                        gap: '4px',
                      }}
                      onClick={handleToggleIA}
                      title={atendimento.ia_pausada ? 'Reativar IA nesta conversa' : 'Pausar IA nesta conversa'}
                    >
                      <span style={{ fontSize: '13px' }}>{atendimento.ia_pausada ? '\u25B6' : '\u23F8'}</span>
                      <span className="wa-header-btn-text">
                        {atendimento.ia_pausada ? 'Reativar IA' : 'Pausar IA'}
                      </span>
                    </button>
                  )}

                  {/* AI Toggle global removido - gerenciado na tela de config */}

                  {/* Refresh */}
                  <button
                    style={styles.headerBtn}
                    onClick={() => {
                      fetchTimeline(selectedPhone, false);
                      fetchConversationWindow(selectedPhone);
                      fetchReceivedMessages(selectedPhone);
                    }}
                    title="Atualizar mensagens"
                  >
                    &#8635;
                  </button>
                </div>
              </div>

              {/* Messages Area */}
              {loadingMessages ? (
                <div style={styles.loadingOverlay}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={styles.spinner} />
                    <p style={{ color: '#667781', marginTop: '12px', fontSize: '13px' }}>
                      Carregando mensagens...
                    </p>
                  </div>
                </div>
              ) : (
                <div
                  ref={messagesAreaRef}
                  className="wa-messages-area"
                  style={styles.messagesArea}
                >
                  {groupedMessages.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '60px 20px', color: '#667781' }}>
                      <div style={{ fontSize: '48px', marginBottom: '12px', opacity: 0.4 }}>
                        &#128172;
                      </div>
                      <p style={{ fontSize: '14px', margin: 0 }}>Nenhuma mensagem nesta conversa</p>
                    </div>
                  )}

                  {groupedMessages.map((item) => {
                    if (item.type === 'date') {
                      return (
                        <div key={item.key} style={styles.dateSeparator}>
                          <span style={styles.dateSeparatorLabel}>{item.label}</span>
                        </div>
                      );
                    }

                    const { msg } = item;
                    const msgStyle = getMessageStyle(msg.tipo, msg);

                    // Mensagens de sistema: renderização centralizada como pill
                    if (msgStyle.isSystem) {
                      return (
                        <div key={item.key} style={{ display: 'flex', justifyContent: 'center', margin: '12px 0' }}>
                          <span style={{
                            background: '#fff', border: '1px solid #e5e7eb', borderRadius: '20px',
                            padding: '6px 16px', fontSize: '12px', color: '#6b7280',
                            textAlign: 'center', maxWidth: '80%', lineHeight: '1.5',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)', whiteSpace: 'pre-line',
                          }}>
                            {msg.mensagem}
                            <span style={{ display: 'block', fontSize: '10px', color: '#9ca3af', marginTop: '2px' }}>
                              {formatTime(msg.data)}
                            </span>
                          </span>
                        </div>
                      );
                    }

                    const isCompact = msg.tipo === 'tool';
                    const isIAMsg = msg.tipo_msg === 'resposta_ia';

                    return (
                      <div key={item.key} style={styles.messageRow(msgStyle.align)}>
                        <div
                          className="wa-message-bubble"
                          style={styles.messageBubble(msgStyle, isCompact)}
                        >
                          {/* Label */}
                          <div style={styles.messageLabel(msgStyle)}>
                            <span>{msgStyle.label}</span>
                            {msg.tipo === 'envio' && msg.tipo_msg && !isIAMsg && (
                              <span
                                style={styles.messageLabelBadge('#e0e7ff', '#3730a3')}
                              >
                                {msg.tipo_msg}
                              </span>
                            )}
                            {(msg.tipo === 'assistant' || isIAMsg) && (
                              <span
                                style={styles.messageLabelBadge('#ddd6fe', '#5b21b6')}
                              >
                                IA
                              </span>
                            )}
                          </div>

                          {/* Media for received messages */}
                          {(msg.tipo === 'recebida' || msg.tipo === 'user') &&
                            renderMessageMedia(msg)}

                          {/* Media for sent messages */}
                          {msg.tipo === 'envio' && renderEnvioMedia(msg)}

                          {/* Content */}
                          <div style={styles.messageContent(isCompact)}>
                            {msg.mensagem || (
                              <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>
                                Sem conteudo
                              </span>
                            )}
                          </div>

                          {/* Tool calls detail */}
                          {msg.extra && msg.tipo === 'tool' && (
                            <div style={styles.toolExtra}>
                              {(() => {
                                try {
                                  const extra =
                                    typeof msg.extra === 'string'
                                      ? JSON.parse(msg.extra)
                                      : msg.extra;
                                  if (Array.isArray(extra)) {
                                    return (
                                      'Tools: ' +
                                      extra.map((tc) => tc.function?.name).filter(Boolean).join(', ')
                                    );
                                  }
                                  if (extra.function?.name) {
                                    return 'Tool: ' + extra.function.name;
                                  }
                                  return '';
                                } catch {
                                  return '';
                                }
                              })()}
                            </div>
                          )}

                          {/* Timestamp + sender info */}
                          <div style={styles.messageTime}>
                            {msg.sender && msg.tipo === 'envio' && !isIAMsg && (
                              <span
                                title={`Enviado por: ${msg.sender}`}
                                style={styles.senderBadge}
                              >
                                {msg.sender.split(' ')[0]}
                              </span>
                            )}
                            {msg._optimistic && (
                              <span style={{ fontSize: '10px', color: '#9ca3af' }}>&#9201;</span>
                            )}
                            <span>{formatTime(msg.data)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  <div ref={messagesEndRef} />
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div
                  style={{
                    padding: '6px 16px',
                    background: '#fee2e2',
                    borderBottom: '1px solid #fecaca',
                    fontSize: '12px',
                    color: '#991b1b',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <span>{error}</span>
                  <button
                    onClick={() => setError(null)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#991b1b',
                      cursor: 'pointer',
                      fontSize: '16px',
                      padding: '0 4px',
                    }}
                  >
                    &#10005;
                  </button>
                </div>
              )}

              {/* Historico mode: info banner */}
              {viewingHistorico && (
                <div style={{
                  padding: '8px 16px',
                  background: '#eff6ff',
                  borderTop: '1px solid #bfdbfe',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}>
                  <span style={{ fontSize: '14px' }}>&#128197;</span>
                  <span style={{ fontSize: '12px', color: '#1e40af', fontWeight: 500 }}>
                    Visualizando atendimento anterior
                  </span>
                  <button
                    style={{
                      marginLeft: '8px',
                      padding: '3px 10px',
                      borderRadius: '6px',
                      border: '1px solid #bfdbfe',
                      background: '#fff',
                      fontSize: '12px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      color: '#1d4ed8',
                    }}
                    onClick={() => {
                      setViewingHistorico(false);
                      navigate(`/whatsapp-conversas/${encodeURIComponent(selectedPhone)}`, { replace: true });
                      fetchTimeline(selectedPhone, true);
                    }}
                  >
                    Ver conversa atual
                  </button>
                </div>
              )}

              {/* Media Preview */}
              {mediaPreview && (
                <div style={styles.mediaPreviewOverlay}>
                  <div style={styles.mediaPreviewContainer}>
                    <div style={styles.mediaPreviewHeader}>
                      <span style={styles.mediaPreviewTitle}>
                        {mediaPreview.type === 'image'
                          ? 'Enviar imagem'
                          : mediaPreview.type === 'video'
                            ? 'Enviar video'
                            : mediaPreview.type === 'audio'
                              ? 'Enviar audio'
                              : 'Enviar documento'}
                      </span>
                      <button
                        style={styles.mediaPreviewClose}
                        onClick={() => {
                          setMediaPreview(null);
                          setCaptionInput('');
                        }}
                      >
                        &#10005;
                      </button>
                    </div>

                    {mediaPreview.type === 'image' && mediaPreview.preview && (
                      <div style={{ textAlign: 'center' }}>
                        <img
                          src={mediaPreview.preview}
                          alt="Preview"
                          style={styles.mediaPreviewImage}
                        />
                      </div>
                    )}

                    {mediaPreview.type !== 'image' && (
                      <div style={styles.mediaPreviewFileInfo}>
                        <span style={{ fontSize: '24px' }}>
                          {mediaPreview.type === 'video'
                            ? '\u{1F3AC}'
                            : mediaPreview.type === 'audio'
                              ? '\u{1F3B5}'
                              : '\u{1F4C4}'}
                        </span>
                        <div>
                          <div style={{ fontWeight: 600 }}>{mediaPreview.filename}</div>
                          <div style={{ fontSize: '11px', color: '#6b7280' }}>
                            {(mediaPreview.file.size / 1024).toFixed(1)} KB
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Caption input (for image, video, document) */}
                    {mediaPreview.type !== 'audio' && (
                      <input
                        type="text"
                        placeholder="Adicionar legenda..."
                        value={captionInput}
                        onChange={(e) => setCaptionInput(e.target.value)}
                        style={styles.mediaPreviewCaption}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSendMedia();
                        }}
                      />
                    )}

                    <div style={styles.mediaPreviewActions}>
                      <button
                        style={styles.mediaPreviewCancel}
                        onClick={() => {
                          setMediaPreview(null);
                          setCaptionInput('');
                        }}
                      >
                        Cancelar
                      </button>
                      <button
                        style={{
                          ...styles.mediaPreviewSend,
                          opacity: sending ? 0.6 : 1,
                          cursor: sending ? 'not-allowed' : 'pointer',
                        }}
                        onClick={handleSendMedia}
                        disabled={sending}
                      >
                        {sending ? 'Enviando...' : 'Enviar'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Input Area */}
              {!mediaPreview && (
                <div style={styles.inputArea}>
                  {isRecording ? (
                    /* Recording Mode */
                    <>
                      <div style={styles.recordingBar}>
                        <div style={styles.recordingDot} />
                        <span style={styles.recordingTimer}>
                          {formatRecordingTime(recordingTime)}
                        </span>
                        <span style={{ fontSize: '13px', color: '#6b7280' }}>Gravando...</span>
                        <button
                          style={styles.recordingCancel}
                          onClick={() => stopRecording(true)}
                        >
                          Cancelar
                        </button>
                      </div>
                      <button
                        style={{
                          ...styles.sendBtn,
                          background: '#ef4444',
                        }}
                        onClick={() => stopRecording(false)}
                        title="Parar e enviar"
                      >
                        &#9632;
                      </button>
                    </>
                  ) : (
                    /* Normal Input Mode */
                    <>
                      {/* Attach */}
                      <div style={styles.attachMenuContainer} data-attach-menu="true">
                        <button
                          style={styles.iconBtn}
                          onClick={() => setShowAttachMenu(!showAttachMenu)}
                          title="Anexar"
                        >
                          &#128206;
                        </button>
                        {showAttachMenu && (
                          <div style={styles.attachMenu}>
                            <button
                              style={styles.attachMenuItem}
                              onClick={() => handleAttachClick('image')}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#f3f4f6';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                              }}
                            >
                              <span style={{ fontSize: '18px' }}>&#128247;</span>
                              Imagem
                            </button>
                            <button
                              style={styles.attachMenuItem}
                              onClick={() => handleAttachClick('video')}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#f3f4f6';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                              }}
                            >
                              <span style={{ fontSize: '18px' }}>&#127909;</span>
                              Video
                            </button>
                            <button
                              style={styles.attachMenuItem}
                              onClick={() => handleAttachClick('audio')}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#f3f4f6';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                              }}
                            >
                              <span style={{ fontSize: '18px' }}>&#127925;</span>
                              Audio
                            </button>
                            <button
                              style={styles.attachMenuItem}
                              onClick={() => handleAttachClick('document')}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#f3f4f6';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                              }}
                            >
                              <span style={{ fontSize: '18px' }}>&#128196;</span>
                              Documento
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Text Input */}
                      <textarea
                        ref={textAreaRef}
                        value={messageInput}
                        onChange={handleTextAreaChange}
                        onKeyDown={handleKeyDown}
                        placeholder="Digite uma mensagem"
                        style={styles.textArea}
                        rows={1}
                        disabled={sending}
                      />

                      {/* Mic / Send */}
                      {messageInput.trim() ? (
                        <button
                          style={sending ? styles.sendBtnDisabled : styles.sendBtn}
                          onClick={handleSendText}
                          disabled={sending}
                          title="Enviar mensagem"
                        >
                          &#10148;
                        </button>
                      ) : (
                        <button
                          style={styles.iconBtn}
                          onClick={handleMicClick}
                          title="Gravar audio"
                        >
                          &#127908;
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Conversation window closed warning */}
              {conversationWindow &&
                !conversationWindow.open &&
                !mediaPreview &&
                !isRecording && (
                  <div style={styles.inputAreaDisabled}>
                    <span style={styles.windowClosedWarning}>
                      &#9888; Janela de conversa fechada - o paciente precisa enviar uma mensagem
                      para reabrir (voce ainda pode enviar templates)
                    </span>
                  </div>
                )}
            </>
          )}
        </div>

        {/* ─── PACIENTE POPUP PANEL ─── */}
        {showPacientePopup && selectedPhone && (
          <div className="wa-contact-panel" style={{ ...styles.contactPanel, zIndex: 20 }}>
            <div style={styles.contactPanelHeader}>
              <button
                style={styles.contactPanelClose}
                onClick={() => setShowPacientePopup(false)}
                title="Fechar"
              >
                &#10005;
              </button>
              <span style={styles.contactPanelTitle}>Dados do Paciente</span>
            </div>

            {loadingPaciente ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={styles.spinner} />
              </div>
            ) : pacienteInfo ? (
              <div style={{ flex: 1, overflowY: 'auto', padding: '0' }}>
                {/* Dados do Paciente */}
                {pacienteInfo.paciente ? (
                  <div style={{ padding: '16px', borderBottom: '1px solid #e5e7eb' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                      <div style={{ fontSize: '15px', fontWeight: 700, color: '#1f2937' }}>
                        {pacienteInfo.paciente.nome}
                      </div>
                      <a
                        href={`/pacientes/${pacienteInfo.paciente.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          fontSize: '11px', color: '#1d4ed8', textDecoration: 'none',
                          padding: '3px 8px', borderRadius: '6px', border: '1px solid #bfdbfe',
                          background: '#eff6ff', fontWeight: 600, whiteSpace: 'nowrap',
                          display: 'inline-flex', alignItems: 'center', gap: '3px',
                        }}
                        title="Abrir ficha do paciente em nova aba"
                      >
                        Ficha &#8599;
                      </a>
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: 1.8 }}>
                      {pacienteInfo.paciente.cpf && <div>CPF: {pacienteInfo.paciente.cpf}</div>}
                      {pacienteInfo.paciente.nascimento && (
                        <div>Nasc: {new Date(pacienteInfo.paciente.nascimento).toLocaleDateString('pt-BR')}</div>
                      )}
                      {pacienteInfo.paciente.telefone && <div>Tel: {pacienteInfo.paciente.telefone}</div>}
                      {pacienteInfo.paciente.email && <div>Email: {pacienteInfo.paciente.email}</div>}
                    </div>
                    {!pacienteInfo.vinculado && (
                      <div style={{ marginTop: '6px', fontSize: '11px', color: '#d97706', background: '#fef3c7', padding: '4px 8px', borderRadius: '6px', display: 'inline-block' }}>
                        Nao vinculado (encontrado por telefone)
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ padding: '16px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
                    Nenhum paciente encontrado para este telefone
                  </div>
                )}

                {/* Protocolos */}
                {pacienteInfo.protocolos && pacienteInfo.protocolos.length > 0 && (
                  <div style={{ padding: '12px 16px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: '#374151', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Protocolos Recentes
                    </div>
                    {pacienteInfo.protocolos.map((prot, idx) => (
                      <div key={idx} style={{ marginBottom: '12px', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                        {/* Protocol header */}
                        <div style={{ padding: '8px 12px', background: '#f9fafb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb' }}>
                          <div>
                            <span style={{ fontSize: '13px', fontWeight: 600, color: '#1f2937' }}>
                              {prot.PROTOCOLO}
                            </span>
                            <span style={{ fontSize: '11px', color: '#6b7280', marginLeft: '6px' }}>
                              {prot.LOCAL} - {new Date(prot.DATA).toLocaleDateString('pt-BR')}
                            </span>
                          </div>
                          <span style={{
                            fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px',
                            background: prot.exames_prontos === prot.total_exames ? '#dcfce7' : '#fef3c7',
                            color: prot.exames_prontos === prot.total_exames ? '#166534' : '#92400e',
                          }}>
                            {prot.exames_prontos}/{prot.total_exames} prontos
                          </span>
                        </div>

                        {/* Exames list */}
                        <div style={{ padding: '6px 12px' }}>
                          {prot.exames && prot.exames.map((ex, j) => (
                            <div key={j} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '12px', borderBottom: j < prot.exames.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                              <span style={{ color: '#374151' }}>{ex.nome || ex.codigo}</span>
                              <span style={{
                                fontWeight: 600, fontSize: '11px',
                                color: ['I', 'E', 'D', 'A'].includes(ex.situacao) ? '#166534' : '#92400e',
                              }}>
                                {ex.status_texto}
                              </span>
                            </div>
                          ))}
                        </div>

                        {/* Action buttons per protocol */}
                        <div style={{ padding: '6px 12px 8px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          <button
                            style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid #d1d5db', background: '#f9fafb', fontSize: '11px', cursor: 'pointer', color: '#374151' }}
                            onClick={() => {
                              const nome = pacienteInfo.paciente?.nome || 'paciente';
                              const prontos = prot.exames_prontos;
                              const total = prot.total_exames;
                              const pendentes = total - prontos;
                              let msg = `Ola ${nome}! Sobre seu protocolo *${prot.PROTOCOLO}*: *${prontos}/${total}* exames prontos.`;
                              if (pendentes > 0) {
                                const pendList = prot.exames?.filter(e => !['I','E','D','A'].includes(e.situacao)).map(e => e.nome || e.codigo).join(', ');
                                msg += `\n\nPendentes: ${pendList}`;
                              } else {
                                msg += '\n\nTodos os exames estao prontos!';
                              }
                              enviarMensagemPronta(msg);
                            }}
                            title="Enviar status dos exames"
                          >
                            Enviar status
                          </button>
                          <button
                            style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid #d1d5db', background: '#f9fafb', fontSize: '11px', cursor: 'pointer', color: '#374151' }}
                            onClick={() => {
                              const lista = prot.exames?.map(e => `- ${e.nome || e.codigo}: ${e.status_texto}`).join('\n') || '';
                              enviarMensagemPronta(`Seus exames (protocolo *${prot.PROTOCOLO}*):\n${lista}`);
                            }}
                            title="Enviar lista de exames"
                          >
                            Lista exames
                          </button>
                          {prot.RESULTADO_LIBERADO && (
                            <>
                              <button
                                style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid #a7f3d0', background: '#f0fdf4', fontSize: '11px', cursor: 'pointer', color: '#166534', fontWeight: 600 }}
                                onClick={() => {
                                  const link = `https://santarita.viptecnologia.com.br/resultados/${prot.ID}`;
                                  enviarMensagemPronta(`Acesse seus resultados do protocolo *${prot.PROTOCOLO}* em:\n${link}`);
                                }}
                                title="Enviar link do resultado"
                              >
                                Enviar link
                              </button>
                              <button
                                style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid #93c5fd', background: '#eff6ff', fontSize: '11px', cursor: 'pointer', color: '#1e40af', fontWeight: 600 }}
                                onClick={() => enviarPdfNoChat(prot.LOCAL, prot.PROTOCOLO, pacienteInfo.paciente?.nome)}
                                disabled={sending}
                                title="Enviar PDF do laudo no chat"
                              >
                                {sending ? '...' : 'Enviar PDF'}
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : null}
          </div>
        )}

        {/* ─── CONTACT DETAILS PANEL ─── */}
        {(contactPanel || loadingContact) && selectedPhone && (
          <div className="wa-contact-panel" style={styles.contactPanel}>
            {/* Header */}
            <div style={styles.contactPanelHeader}>
              <button
                style={styles.contactPanelClose}
                onClick={() => setContactPanel(null)}
                title="Fechar"
              >
                &#10005;
              </button>
              <span style={styles.contactPanelTitle}>Detalhes do contato</span>
            </div>

            {loadingContact ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={styles.spinner} />
              </div>
            ) : contactPanel && (
              <>
                {/* Avatar + Nome + Telefone */}
                <div>
                  <div style={styles.contactAvatarBig}>
                    {(contactPanel.nome || contactPanel.phone || '?')[0]}
                  </div>
                  <div style={styles.contactName}>
                    {contactPanel.nome || 'Sem nome'}
                  </div>
                  <div style={styles.contactPhone}>
                    {contactPanel.phone?.replace(/^55(\d{2})(\d{4,5})(\d{4})$/, '+55 ($1) $2-$3') || contactPanel.phone}
                  </div>
                  <button
                    onClick={() => navigate(`/whatsapp-contatos/${encodeURIComponent(contactPanel.phone)}`)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      marginTop: '12px',
                      background: '#1976d2',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#1565c0'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#1976d2'}
                  >
                    Ver perfil completo
                  </button>
                </div>

                {/* Estatísticas */}
                <div style={styles.contactSection}>
                  <div style={styles.contactSectionTitle}>Mensagens</div>
                  <div style={styles.contactStatGrid}>
                    <div style={styles.contactStatCard('#dcfce7')}>
                      <div style={styles.contactStatNumber}>{contactPanel.total_recebidas}</div>
                      <div style={styles.contactStatLabel}>Recebidas</div>
                    </div>
                    <div style={styles.contactStatCard('#dbeafe')}>
                      <div style={styles.contactStatNumber}>{contactPanel.total_enviadas}</div>
                      <div style={styles.contactStatLabel}>Enviadas</div>
                    </div>
                    <div style={styles.contactStatCard('#ede9fe')}>
                      <div style={styles.contactStatNumber}>{contactPanel.total_ia}</div>
                      <div style={styles.contactStatLabel}>IA</div>
                    </div>
                  </div>
                </div>

                {/* Datas */}
                <div style={styles.contactSection}>
                  <div style={styles.contactSectionTitle}>Atividade</div>
                  <div style={styles.contactRow}>
                    <span style={styles.contactLabel}>
                      <span style={{ fontSize: '14px' }}>&#128197;</span> Primeiro contato
                    </span>
                    <span style={styles.contactValue}>
                      {contactPanel.primeiro_contato
                        ? new Date(contactPanel.primeiro_contato).toLocaleDateString('pt-BR')
                        : '—'}
                    </span>
                  </div>
                  <div style={styles.contactRow}>
                    <span style={styles.contactLabel}>
                      <span style={{ fontSize: '14px' }}>&#128336;</span> Ultimo contato
                    </span>
                    <span style={styles.contactValue}>
                      {contactPanel.ultimo_contato
                        ? new Date(contactPanel.ultimo_contato).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                        : '—'}
                    </span>
                  </div>
                  <div style={styles.contactRow}>
                    <span style={styles.contactLabel}>
                      <span style={{ fontSize: '14px' }}>&#9202;</span> Janela expira
                    </span>
                    <span style={styles.contactValue}>
                      {contactPanel.janela_expira
                        ? (new Date(contactPanel.janela_expira) > new Date()
                          ? formatCountdown(contactPanel.janela_expira)
                          : 'Expirada')
                        : '—'}
                    </span>
                  </div>
                </div>

                {/* Templates */}
                {contactPanel.templates_distintos > 0 && (
                  <div style={styles.contactSection}>
                    <div style={styles.contactSectionTitle}>Templates</div>
                    <div style={styles.contactRow}>
                      <span style={styles.contactLabel}>
                        <span style={{ fontSize: '14px' }}>&#128196;</span> Templates usados
                      </span>
                      <span style={styles.contactValue}>{contactPanel.templates_distintos}</span>
                    </div>
                    {contactPanel.ultimo_template && (
                      <div style={styles.contactRow}>
                        <span style={styles.contactLabel}>
                          <span style={{ fontSize: '14px' }}>&#128172;</span> Ultimo template
                        </span>
                        <span style={styles.contactValue} title={contactPanel.ultimo_template}>
                          {contactPanel.ultimo_template}
                        </span>
                      </div>
                    )}
                    {contactPanel.ultimo_enviado_por && (
                      <div style={styles.contactRow}>
                        <span style={styles.contactLabel}>
                          <span style={{ fontSize: '14px' }}>&#128100;</span> Enviado por
                        </span>
                        <span style={styles.contactValue}>{contactPanel.ultimo_enviado_por}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Paciente vinculado */}
                {contactPanel.paciente && (
                  <div style={styles.contactSection}>
                    <div style={styles.contactSectionTitle}>Paciente vinculado</div>
                    <div style={styles.contactPacienteCard}>
                      <div style={styles.contactPacienteName}>
                        {contactPanel.paciente.NOME}
                      </div>
                      <div style={styles.contactPacienteInfo}>
                        {contactPanel.paciente.CPF && (
                          <div>CPF: {contactPanel.paciente.CPF}</div>
                        )}
                        {contactPanel.paciente.DATA_NASCIMENTO && (
                          <div>Nasc: {new Date(contactPanel.paciente.DATA_NASCIMENTO).toLocaleDateString('pt-BR')}</div>
                        )}
                        {contactPanel.paciente.SEXO && (
                          <div>Sexo: {contactPanel.paciente.SEXO === 'M' ? 'Masculino' : contactPanel.paciente.SEXO === 'F' ? 'Feminino' : contactPanel.paciente.SEXO}</div>
                        )}
                        {contactPanel.paciente.TELEFONE_CELULAR && (
                          <div>Tel: {contactPanel.paciente.TELEFONE_CELULAR}</div>
                        )}
                        {contactPanel.paciente.EMAIL && (
                          <div>Email: {contactPanel.paciente.EMAIL}</div>
                        )}
                        {contactPanel.paciente.CIDADE && (
                          <div>{[contactPanel.paciente.BAIRRO, contactPanel.paciente.CIDADE].filter(Boolean).join(' - ')}</div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {!contactPanel.paciente && contactPanel.paciente_codigo && (
                  <div style={styles.contactSection}>
                    <div style={styles.contactSectionTitle}>Paciente vinculado</div>
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>
                      Codigo: {contactPanel.paciente_codigo} (dados indisponiveis)
                    </div>
                  </div>
                )}

                {/* Pacientes encontrados por telefone (sem vínculo direto) */}
                {!contactPanel.paciente && !contactPanel.paciente_codigo && contactPanel.pacientes_telefone && contactPanel.pacientes_telefone.length > 0 && (
                  <div style={styles.contactSection}>
                    <div style={styles.contactSectionTitle}>
                      Pacientes com este telefone ({contactPanel.pacientes_telefone.length})
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                      Nenhum vinculo confirmado. Pacientes encontrados pelo numero:
                    </div>
                    {contactPanel.pacientes_telefone.map((pac) => (
                      <div key={pac.ID} style={{
                        padding: '8px 10px',
                        marginBottom: '6px',
                        background: '#f8fafc',
                        borderRadius: '6px',
                        border: '1px solid #e2e8f0',
                        fontSize: '13px',
                      }}>
                        <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: '2px' }}>
                          {pac.NOME}
                        </div>
                        <div style={{ color: '#64748b', fontSize: '12px', lineHeight: '1.5' }}>
                          {pac.CPF && <div>CPF: {pac.CPF}</div>}
                          {pac.DATA_NASCIMENTO && (
                            <div>Nasc: {new Date(pac.DATA_NASCIMENTO).toLocaleDateString('pt-BR')}</div>
                          )}
                          {pac.TELEFONE_CELULAR && <div>Cel: {pac.TELEFONE_CELULAR}</div>}
                          {pac.TELEFONE && <div>Tel: {pac.TELEFONE}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Nenhum paciente encontrado */}
                {!contactPanel.paciente && !contactPanel.paciente_codigo && (!contactPanel.pacientes_telefone || contactPanel.pacientes_telefone.length === 0) && (
                  <div style={styles.contactSection}>
                    <div style={styles.contactSectionTitle}>Paciente</div>
                    <div style={{ fontSize: '13px', color: '#9ca3af' }}>
                      Nenhum paciente vinculado ou encontrado por telefone.
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default WhatsAppConversas;
